import { EfficientListLoader } from "@faire/deprecated-retailer-visitor-shared/components/EfficientListLoader/EfficientListLoader";
import { withFallback } from "@faire/deprecated-retailer-visitor-shared/components/GhostLoading/helpers";
import { PAGE_TYPE } from "@faire/deprecated-retailer-visitor-shared/consts/PAGE_TYPE";
import { ILeanProductTileWithBrand } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/__internal__/adapters/LeanProtos/ILeanProductTile";
import { SaveToBoardProvider } from "@faire/deprecated-retailer-visitor-shared/features/RetailerBoards/Context";
import { isLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { Category } from "@faire/deprecated-retailer-visitor-shared/models/Category/Category";
import { Tile } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/ProductOrBrandGrid/__internal__/ProductFirst/__internal__/MarketplaceProductList/__internal__/Tile";
import { moveProductToFrontOfArray } from "@faire/deprecated-retailer-visitor-shared/views/product/utils/moveProductToFrontOfArray";
import { trackCategoriesProductBatchView } from "@faire/web-api--source/events/categories/view/productBatch";
import { trackRetailerOnboardingCategoryPageHighlightedProductView } from "@faire/web-api--source/events/retailerOnboarding/view/categoryPageHighlightedProduct";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { IRawQueryParamsProps } from "@faire/web/common/query-params/IQueryParamsProps";
import { withQueryParams } from "@faire/web/common/query-params/withQueryParams";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import isChromatic from "chromatic/isChromatic";
import * as React from "react";
import type { JSX } from "react";
import { withRouter } from "react-router-dom";
import styled, { css } from "styled-components";

export namespace MarketplaceProductList {
  export interface IProps extends IRawQueryParamsProps {
    category: Category | undefined;
    filterKeys: string[];
    items: ILeanProductTileWithBrand[];
    isLoading: boolean;
    pageNumber?: number;
    showVerticalImagery?: boolean;
    requestId: string | undefined;
    scrollRef?: React.Ref<HTMLDivElement | null>;
    title?: string | undefined;
    pageType?: PAGE_TYPE;
    isScrollingToTop?: boolean;
    onFirstBatchVisible?: () => void;
    itemsPerRow?: ResponsiveValues<number>;
  }
}

const MarketplaceProductListBase: React.FunctionComponent<
  MarketplaceProductList.IProps
> = ({
  category,
  filterKeys,
  isLoading,
  items,
  pageType,
  queryParams,
  requestId,
  scrollRef,
  showVerticalImagery,
  title,
  isScrollingToTop,
  onFirstBatchVisible,
  itemsPerRow: overrideItemsPerRow,
}) => {
  const isSSR = useIsSSR();

  const renderTile = (tileData: ILeanProductTileWithBrand | undefined) => (
    <Tile
      tileData={tileData}
      product={tileData?.product}
      title={title}
      pageType={pageType}
      filterKeys={filterKeys}
      category={category}
      showVerticalImagery={showVerticalImagery}
      requestId={requestId}
    />
  );

  const renderProductTileBatchPlaceholder = (
    placeholderItems?: (ILeanProductTileWithBrand | undefined)[],
    tileBatchIndex?: number
  ): JSX.Element => {
    return (
      <React.Fragment key={`placeholder-batch-${tileBatchIndex}`}>
        {placeholderItems?.map((_, index) => (
          <React.Fragment key={`placeholder-${index}`}>
            {renderTile(undefined)}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  };

  const onProductBatchVisible = (
    items: Array<ILeanProductTile | undefined>,
    tileBatchIndex: number
  ) => {
    if (tileBatchIndex === 0) {
      onFirstBatchVisible?.();
    }

    if (requestId) {
      trackCategoriesProductBatchView(
        requestId,
        items
          .map((i) => i?.product?.token)
          .filter(isNotUndefined)
          .join(" ")
      );
    }
  };

  const itemsPerRow = useResponsiveValue(
    overrideItemsPerRow ?? {
      mobileAndAbove: 2,
      tabletAndAbove: 4,
    }
  );

  const products = items.length > 0 ? items : withFallback(items, 20);

  //only highlight product if it is first page (no page query param)
  const displayProducts = queryParams.get("page")
    ? products
    : moveProductToFrontOfArray<ILeanProductTileWithBrand>(
        products,
        queryParams.get("refP"),
        trackRetailerOnboardingCategoryPageHighlightedProductView
      );

  const testIdAttribute = isLoading
    ? "marketplace-product-list-loading"
    : "marketplace-product-list";
  return (
    <Wrapper
      $enableSearchResultCleanup={isLoggedInRetailer()}
      data-test-id={testIdAttribute}
      $columns={itemsPerRow}
      ref={scrollRef}
    >
      <SaveToBoardProvider value={{ trackOrigin: "category-page" }}>
        <EfficientListLoader
          hashKey={requestId}
          data={displayProducts}
          batchSize={itemsPerRow * 2}
          renderComponent={renderTile}
          // Remember to update the placeholder if the tile changes
          placeholderFn={renderProductTileBatchPlaceholder}
          forceRender={isSSR || isChromatic()}
          onBatchVisible={onProductBatchVisible}
          filterFakeTile
          threshold={-400}
          renderPlaceholderForNonFirstBatch={isScrollingToTop}
        />
      </SaveToBoardProvider>
    </Wrapper>
  );
};

export const MarketplaceProductList = withRouter(
  withQueryParams(MarketplaceProductListBase)
);

const Wrapper = styled.div<{
  $columns: number;
  $enableSearchResultCleanup: boolean;
}>`
  display: grid;
  align-self: stretch;
  //minmax(0, 1fr) ensures text overflow happens properly
  grid-template-columns:
    [start] repeat(${(props) => props.$columns}, minmax(0, 1fr))
    [end];

  ${responsiveMediaQueries.tabletAndAbove} {
    margin: ${({ $enableSearchResultCleanup }) =>
      $enableSearchResultCleanup ? undefined : "0 -8px"};
  }

  ${({ $enableSearchResultCleanup }) =>
    $enableSearchResultCleanup
      ? css`
          column-gap: ${getSpacing("1x")};
          row-gap: ${getSpacing("3x")};
          padding-top: ${getSpacing("1x")};
        `
      : "margin: 0 -4px"}
`;
